import React, { Fragment } from 'react';
import { formatCredits, getSitePrice } from '../../utils/credits';
import ScreeningDatePicker from './ScreeningDatePicker';
import KeyValidityDisplay from './KeyValidityDisplay';
import { getLanguageByCode } from '../../utils/languageCodes';

const OrderModalContent = ({ modalType, order, user, screeningDate, setScreeningDate, minimumHours = 4, minimumHoursLabel = null }) => {
    if (!order) return null;

    // Price for this site's territory and tier on the chosen screening date
    const calculatePrice = () => {
        if (!screeningDate) return null;
        return getSitePrice(order.version, user.parentOrganization, screeningDate, 'number');
    };

    const price = calculatePrice();
    const hasEnoughCredits = price !== null && (user.parentOrganization.creditAmount - price) >= 0;

    // ORDER modal content
    if (modalType === 'ORDER') {
        return (
            <Fragment>
                <div className="bg-body-tertiary bg-opacity-10 p-3 border-start border-primary border-3">
                    <div className="mb-2">
                        <span className="text-primary text-uppercase">{order.movie.title}</span> | {order.version.name}
                    </div>

                    {order.version.assetDetails && (
                        <Fragment>
                            {order.version.assetDetails?.audioLanguage?.length > 0 && (
                                <Fragment>
                                    <span className="fas fa-language me-2" />
                                    {order.version.assetDetails?.audioLanguage.map(code => getLanguageByCode(code)).join(', ')}
                                </Fragment>
                            )}

                            {order.version.assetDetails?.subtitlesLanguage?.length > 0 && (
                                <Fragment>
                                    <span className="fas fa-closed-captioning ms-3 me-2" />
                                    {order.version.assetDetails?.subtitlesLanguage.map(code => getLanguageByCode(code)).join(', ')}
                                </Fragment>
                            )}

                            <span className="fas fa-video-camera ms-3 me-2" />
                            {order.version.assetDetails?.resolution} | {order.version.assetDetails?.ratio?.toUpperCase()} | {order.version.assetDetails?.dimensions}

                            <span className="fas fa-volume-off ms-3 me-2" />
                            {order.version.assetDetails?.audioConfig}
                        </Fragment>
                    )}
                </div>

                <h5 className="text-expand text-uppercase mt-4">Screening date & time</h5>
                <div className="bg-body-tertiary bg-opacity-10 p-3 border-start border-primary border-3">
                    <ScreeningDatePicker
                        screeningDate={screeningDate}
                        setScreeningDate={setScreeningDate}
                        minimumHours={minimumHours}
                        minimumHoursLabel={minimumHoursLabel}
                    />
                </div>

                {!screeningDate ? (
                    <p className="text-primary lead mt-3 text-center">
                        <span className="fas fa-calendar me-2" />
                        Select a screening date to see the price
                    </p>
                ) : price === null ? (
                    <p className="text-danger lead mt-3 text-center">
                        <span className="fas fa-exclamation-circle me-3" />
                        No pricing available for the selected date
                    </p>
                ) : !hasEnoughCredits ? (
                    <p className="text-danger lead mt-3 text-center">
                        <span className="fas fa-warning me-3" />
                        Not enough credits. You need {formatCredits(price - user.parentOrganization.creditAmount)} more credits.
                    </p>
                ) : (
                    <Fragment>
                        <h5 className="text-expand text-uppercase mt-4">Key validity</h5>
                        <KeyValidityDisplay order={order} screeningDate={screeningDate} />
                    </Fragment>
                )}
            </Fragment>
        );
    }

    // ACCEPT modal content
    if (modalType === 'ACCEPT') {
        return (
            <div>
                <p className="text-primary mb-3">Your request was accepted under the following conditions:</p>

                {order.screeningTerms && (
                    <div className="border-start border-primary bg-black bg-opacity-50 my-3 p-3">
                        <div className="mb-2">
                            <span className="fas fa-file me-2 text-primary" />
                            <strong>Screening Terms</strong>
                        </div>
                        <p className="mb-0">{order.screeningTerms}</p>
                    </div>
                )}

                {order.bacCorrection && (
                    <div className="border-start border-primary bg-black bg-opacity-50 my-3 p-3">
                        {order.bacCorrection.screeningDateCorrection && (
                            <div className="mb-2">
                                <span className="fas fa-calendar-check me-2 text-primary" />
                                <strong>Screening date:</strong> {new Date(order.bacCorrection.screeningDateCorrection).toLocaleString()}
                            </div>
                        )}

                        {order.bacCorrection.defaultHirePeriodCorrection && (
                            <div className="mb-2">
                                <span className="fas fa-clock me-2 text-primary" />
                                <strong>Hire period:</strong> {order.bacCorrection.defaultHirePeriodCorrection} {order.bacCorrection.hirePeriodTypeCorrection}
                            </div>
                        )}

                        {order.bacCorrection.priceCorrection !== undefined && order.bacCorrection.priceCorrection !== null && (
                            <>
                                <div className="mb-2">
                                    <span className="fas fa-coins me-2 text-primary" />
                                    <strong>Price correction:</strong> {formatCredits(order.bacCorrection.priceCorrection)} credits
                                </div>

                                <div className="mb-0">
                                    <span className="fas fa-cash-register me-2 text-primary" />
                                    <strong>Final price:</strong> {/* What was paid plus the correction — not a fresh price lookup,
                                        which the version's prices may have moved away from since. */}
                                    {formatCredits(Number(order.downpayment) + Number(order.bacCorrection.priceCorrection))} credits
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // DECLINE modal content
    if (modalType === 'DECLINE') {
        return (
            <div>
                <p className="text-danger mb-3">Are you sure you want to decline this proposal?</p>
                <div className="alert alert-warning">
                    <span className="fas fa-info-circle me-2" />
                    Your downpayment will be refunded to your account.
                </div>
            </div>
        );
    }

    return null;
};

export default OrderModalContent;
